import React, { useState } from 'react'
import { setCookie } from 'react-use-cookie'

import { checkLocale, getLocale } from '@toasttab/buffet-pui-locale-utilities'

import { ButtonType } from 'src/apollo/sites'
import { ToggleInput } from 'src/shared/components/common/forms'
import {
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalOverlay
} from 'src/shared/components/common/modal'
import { t } from 'src/translations/src'
import { ScreenWidth, useIsMobile } from 'src/shared/js/utils/WindowContext'

import { BrandedButton } from 'shared/components/common/button'

import { LocaleOption, localeOptions, OO_SELECTED_LANGUAGE } from './common'

export function LocaleSelectorModal({
  isOpen,
  onClose
}: {
  isOpen: boolean
  onClose: () => void
}) {
  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay fadeIn fadeOut />
      <LocaleSelectorModalContent onClose={onClose} />
    </Modal>
  )
}

export function LocaleSelectorModalContent({
  onClose
}: {
  onClose: () => void
}) {
  const [selectedLocale, setSelectedLocale] = useState(getLocale())

  const handleLocaleChange = (locale: string) => {
    setSelectedLocale(checkLocale(locale))
  }

  const handleConfirm = () => {
    setCookie(OO_SELECTED_LANGUAGE, selectedLocale)
    window.location.reload()
  }

  return (
    <ModalContent
      wrapperClassName='localeSelector-wrapper'
      contentClassName='localeSelector-modalContent modalContentV2 fullScreenMobile'
      ariaLabelledBy='localeSelectorRadioGroup'
      disableFocusTrap={true}
    >
      <LocaleSelectorModalTitle />
      <LocaleRadioGroup
        selectedLocale={selectedLocale}
        onChange={handleLocaleChange}
      />
      <LocaleSelectorFooter onCancel={onClose} onConfirm={handleConfirm} />
    </ModalContent>
  )
}

/**
 * Renders the radio group for locale selection.
 */
function LocaleRadioGroup({
  selectedLocale,
  onChange
}: {
  selectedLocale: string
  onChange: (locale: string) => void
}) {
  const isMobile = useIsMobile(ScreenWidth.EXTRA_SMALL)

  return (
    <div className='localeSelector radioGroupContainer'>
      {isMobile && <h2>{t('locale-selector.modal.title')}</h2>}
      <div
        role='radiogroup'
        aria-label={t('locale-selector.modal.select-language')}
        className='localeSelector radioGroup'
      >
        {localeOptions.map((option: LocaleOption, index) => (
          <div
            key={`locale-option-${index}`}
            className='localeSelector radioOption'
          >
            <ToggleInput
              checked={selectedLocale === option.locale}
              onChange={() => onChange(option.locale)}
              name={`locale-${option.locale}`}
              id={`locale-${option.locale}`}
              type='radio'
              autoFocus={selectedLocale === option.locale}
            >
              {option.label}
            </ToggleInput>
          </div>
        ))}
      </div>
    </div>
  )
}

/**
 * Renders the footer buttons for the locale selector modal.
 */
function LocaleSelectorFooter({
  onCancel,
  onConfirm
}: {
  onCancel: () => void
  onConfirm: () => void
}) {
  return (
    <div className='localeSelector footerButtons'>
      <BrandedButton variant={ButtonType.Secondary} onClick={onCancel}>
        {t('locale-selector.button.cancel')}
      </BrandedButton>
      <BrandedButton variant={ButtonType.Primary} onClick={onConfirm}>
        {t('locale-selector.button.confirm')}
      </BrandedButton>
    </div>
  )
}

function LocaleSelectorModalTitle() {
  const isMobile = useIsMobile(ScreenWidth.EXTRA_SMALL)

  return (
    <div className='modalHeader centered'>
      {!isMobile && <h2>{t('locale-selector.modal.title')}</h2>}
      <ModalCloseButton />
    </div>
  )
}
